import {
    type FocusEvent,
    type MouseEvent,
    type PointerEvent,
    type RefObject,
    useEffect,
    useRef,
    useState,
} from "react";
import { flushSync } from "react-dom";
import {
    findTaskControl,
    getTaskControlOf,
    type TaskCardControl,
} from "./TaskChipButton";

interface UseTaskCardArmingOptions {
    cardRef: RefObject<HTMLElement | null>;
    /** Something already targets the card, such as a shortcut opening one of its controls. */
    isTargeted: boolean;
}

const hasModifierKey = (event: MouseEvent<HTMLElement>) =>
    event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

/**
 * Whether a board card has mounted its controls (pickers, menus, tooltips):
 * from its first hover with no button held, so a drag passing over cards
 * arms none, its first own focus, or `isTargeted`, and for good after. Until
 * then the card provides `OverlayDeferralContext` so its triggers draw
 * without their controls. A plain click on a trigger marked with
 * `taskControlProps` before that (touch, a screen reader) arms the card and
 * hands the click to that control: a field chip's picker, the kebab menu or
 * Add field. Spread `armProps` on the card.
 */
export const useTaskCardArming = ({
    cardRef,
    isTargeted,
}: UseTaskCardArmingOptions) => {
    const [isArmed, setIsArmed] = useState(isTargeted);
    const clickedControlRef = useRef<TaskCardControl | null>(null);
    if (isTargeted && !isArmed) {
        setIsArmed(true);
    }

    useEffect(() => {
        const card = cardRef.current;
        if (!isArmed || clickedControlRef.current === null || card === null) {
            return undefined;
        }
        // Once the arming click is over: a menu opened during it would take
        // that click, aimed at the replaced stand-in, as a press outside it.
        const timeout = setTimeout(() => {
            const control = clickedControlRef.current;
            clickedControlRef.current = null;
            if (control !== null) {
                findTaskControl(card, control)?.click();
            }
        });
        return () => clearTimeout(timeout);
    }, [isArmed, cardRef]);

    if (isArmed) {
        return { isArmed, armProps: {} };
    }
    const arm = () => setIsArmed(true);
    // Pointer events update at a lower priority than the press that can
    // follow at once: committed later, the press would land on a stand-in.
    const armOnHover = ({ buttons }: PointerEvent<HTMLElement>) => {
        if (buttons === 0) {
            flushSync(arm);
        }
    };
    return {
        isArmed,
        armProps: {
            onPointerOver: armOnHover,
            onPointerMove: armOnHover,
            onFocusCapture: ({
                target,
                currentTarget,
            }: FocusEvent<HTMLElement>) => {
                if (target === currentTarget) {
                    arm();
                }
            },
            onClickCapture: (event: MouseEvent<HTMLElement>) => {
                if (!hasModifierKey(event) && event.target instanceof Element) {
                    clickedControlRef.current = getTaskControlOf(event.target);
                }
                arm();
            },
        },
    };
};
